import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft, Loader2, Download, RefreshCw, FileSpreadsheet,
    AlertTriangle, Receipt, CalendarRange, Scale, Plus, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StripeReconcileWidget from "@/components/coach/StripeReconcileWidget";
import RenewalHealthWidget from "@/components/coach/RenewalHealthWidget";
import CardOnFileHealthWidget from "@/components/coach/CardOnFileHealthWidget";
import SendAccountantMonthCard from "@/components/coach/SendAccountantMonthCard";
import StripeReconciliationWidget from "@/components/coach/StripeReconciliationWidget";
import MoneyTabs from "@/components/coach/MoneyTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const $ = (cents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
        .format((cents || 0) / 100);

function thisYear() { return new Date().getFullYear(); }

function presetOptions() {
    const y = thisYear();
    // Grouped by granularity so the coach can drill from daily → yearly
    // without leaving the page. Order matches typical reporting cadence.
    return [
        // Daily
        { value: "today", label: "Today", group: "Daily" },
        { value: "yesterday", label: "Yesterday", group: "Daily" },
        // Weekly
        { value: "this_week", label: "This week", group: "Weekly" },
        { value: "last_week", label: "Last week", group: "Weekly" },
        { value: "last_7d", label: "Last 7 days", group: "Weekly" },
        // Monthly
        { value: "this_month", label: "This month", group: "Monthly" },
        { value: "last_month", label: "Last month", group: "Monthly" },
        { value: "last_30d", label: "Last 30 days", group: "Monthly" },
        // Quarterly
        { value: "this_quarter", label: "This quarter", group: "Quarterly" },
        { value: "last_quarter", label: "Last quarter", group: "Quarterly" },
        { value: "last_90d", label: "Last 90 days", group: "Quarterly" },
        { value: `q1_${y}`, label: `Q1 ${y}`, group: "Quarterly" },
        { value: `q2_${y}`, label: `Q2 ${y}`, group: "Quarterly" },
        { value: `q3_${y}`, label: `Q3 ${y}`, group: "Quarterly" },
        { value: `q4_${y}`, label: `Q4 ${y}`, group: "Quarterly" },
        // Yearly
        { value: "ytd", label: `YTD ${y}`, group: "Yearly" },
        { value: "last_year", label: `Last year (${y - 1})`, group: "Yearly" },
    ];
}

const KIND_LABEL = {
    charge: "Charge",
    refund: "Refund",
    dispute_funds_withdrawn: "Dispute lost",
    dispute_funds_reinstated: "Dispute won",
    manual_payment: "Manual",
    manual_payment_void: "Void",
};

const KIND_TONE = {
    charge: "bg-emerald-900/40 text-emerald-300",
    refund: "bg-amber-900/40 text-amber-300",
    dispute_funds_withdrawn: "bg-red-900/40 text-red-300",
    dispute_funds_reinstated: "bg-emerald-900/40 text-emerald-300",
    manual_payment: "bg-blue-900/40 text-blue-300",
    manual_payment_void: "bg-zinc-800 text-zinc-400 line-through",
};

export default function Accounting() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [preset, setPreset] = useState("ytd");
    const [basis, setBasis] = useState("cash");
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [busy, setBusy] = useState(true);
    const [backfilling, setBackfilling] = useState(false);
    const [refundOpen, setRefundOpen] = useState(null);  // {event}
    const [logPayOpen, setLogPayOpen] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") {
            toast.error("Owner-only");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const [sr, er] = await Promise.all([
                axios.get(`${API}/coach/accounting/summary`, {
                    params: { preset, basis }, withCredentials: true,
                }),
                axios.get(`${API}/coach/accounting/events`, {
                    params: { preset, limit: 1000 }, withCredentials: true,
                }),
            ]);
            setSummary(sr.data);
            setEvents(er.data.events || []);
        } catch (e) {
            toast.error("Couldn't load accounting data", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (user?.role === "owner") load(); /* eslint-disable-next-line */ }, [user, preset, basis]);

    const downloadCSV = async () => {
        try {
            const r = await axios.get(`${API}/coach/accounting/export.csv`, {
                params: { preset }, withCredentials: true, responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `revenue_${preset}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("CSV export failed", { description: e?.response?.data?.detail });
        }
    };

    const downloadXLSX = async () => {
        try {
            const r = await axios.get(`${API}/coach/accounting/export.xlsx`, {
                params: { preset }, withCredentials: true, responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `maxsc_revenue_${preset}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Excel downloaded — send to bookkeeper");
        } catch (e) {
            toast.error("Excel export failed", { description: e?.response?.data?.detail });
        }
    };

    const voidPayment = async (ev) => {
        const reason = window.prompt(`Void this ${ev.metadata?.method || "manual"} payment of $${((ev.gross_cents||0)/100).toFixed(2)}? Reason:`, "Logged in error");
        if (reason === null) return;
        try {
            await axios.post(`${API}/coach/accounting/manual-payment/${ev.event_id}/void`,
                { reason: reason || "voided" }, { withCredentials: true });
            toast.success("Payment voided");
            load();
        } catch (e) {
            toast.error("Void failed", { description: e?.response?.data?.detail });
        }
    };

    const runBackfill = async () => {
        if (!window.confirm("Pull historical Stripe charges into the revenue ledger? (Idempotent — safe to re-run.)")) return;
        setBackfilling(true);
        try {
            const r = await axios.post(`${API}/coach/accounting/backfill`, null, {
                params: { days: 365 }, withCredentials: true,
            });
            const c = r.data?.counts || {};
            toast.success(`Backfilled: ${c.charges || 0} charges · ${c.refunds || 0} refunds · ${c.disputes || 0} disputes`);
            load();
        } catch (e) {
            toast.error("Backfill failed", { description: e?.response?.data?.detail });
        } finally {
            setBackfilling(false);
        }
    };

    // One-shot repair for legacy malformed revenue_events rows (those with
    // `amount_cents` instead of `gross_cents`, missing `sku_category`, etc.).
    // Idempotent — safe to re-run; only touches rows that haven't been repaired yet.
    const [repairing, setRepairing] = useState(false);
    const runRepair = async () => {
        if (!window.confirm("Re-shape any old charge rows that didn't write proper gross/tax/fee fields so they show up here? Safe to re-run any time.")) return;
        setRepairing(true);
        try {
            const r = await axios.post(`${API}/coach/accounting/repair-malformed-events`, null, {
                withCredentials: true,
            });
            const repaired = r.data?.repaired || 0;
            if (repaired === 0) {
                toast.success("Nothing to repair — all charges already have the correct shape.");
            } else {
                toast.success(`Repaired ${repaired} charge row${repaired === 1 ? "" : "s"}. Refreshing totals…`);
            }
            load();
        } catch (e) {
            toast.error("Repair failed", { description: e?.response?.data?.detail });
        } finally {
            setRepairing(false);
        }
    };

    const totals = summary?.totals || {};
    const wa = summary?.wa_estimates || {};
    const byCat = useMemo(() => Object.entries(summary?.by_category || {}), [summary]);

    if (loading || !user || busy) {
        return (
            <div data-testid="accounting-loading" className="min-h-screen bg-black text-white flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="coach-accounting" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-6 py-10">
                <MoneyTabs />
                <button
                    type="button"
                    onClick={() => navigate("/coach")}
                    className="text-zinc-400 hover:text-white text-sm flex items-center gap-2 mb-6"
                    data-testid="accounting-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to coach
                </button>

                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-3">
                            <Scale className="w-7 h-7 text-emerald-400" />
                            <h1 className="text-3xl font-bold">Accounting</h1>
                        </div>
                        <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
                            The single source of revenue truth. Every Stripe charge, refund, and dispute lands here so your CPA never has to chase Stripe's dashboard.
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <select
                            data-testid="accounting-preset"
                            value={preset}
                            onChange={(e) => setPreset(e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
                        >
                            {(() => {
                                // Render grouped <optgroup> blocks so the
                                // coach can scan Daily / Weekly / Monthly /
                                // Quarterly / Yearly buckets at a glance.
                                const opts = presetOptions();
                                const groups = [];
                                let current = null;
                                opts.forEach((o) => {
                                    if (!current || current.label !== o.group) {
                                        current = { label: o.group, items: [] };
                                        groups.push(current);
                                    }
                                    current.items.push(o);
                                });
                                return groups.map((g) => (
                                    <optgroup key={g.label} label={g.label}>
                                        {g.items.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </optgroup>
                                ));
                            })()}
                        </select>
                        <div className="flex bg-zinc-900 rounded border border-zinc-700 overflow-hidden text-sm">
                            <button
                                type="button"
                                onClick={() => setBasis("cash")}
                                className={`px-3 py-2 ${basis === "cash" ? "bg-emerald-700 text-white" : "text-zinc-400"}`}
                                data-testid="accounting-basis-cash"
                            >Cash</button>
                            <button
                                type="button"
                                onClick={() => setBasis("accrual")}
                                className={`px-3 py-2 ${basis === "accrual" ? "bg-emerald-700 text-white" : "text-zinc-400"}`}
                                data-testid="accounting-basis-accrual"
                            >Accrual</button>
                        </div>
                        <button
                            type="button"
                            onClick={() => setLogPayOpen(true)}
                            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600 rounded px-3 py-2 text-sm"
                            data-testid="accounting-log-payment"
                        ><Plus className="w-4 h-4" /> Log payment</button>
                        <button
                            type="button"
                            onClick={downloadXLSX}
                            className="flex items-center gap-2 bg-emerald-800/40 hover:bg-emerald-700/50 border border-emerald-700 rounded px-3 py-2 text-sm"
                            data-testid="accounting-export-xlsx"
                        ><FileSpreadsheet className="w-4 h-4" /> Excel</button>
                        <button
                            type="button"
                            onClick={downloadCSV}
                            className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
                            data-testid="accounting-export-csv"
                        ><Download className="w-4 h-4" /> CSV</button>
                        <button
                            type="button"
                            onClick={runBackfill}
                            disabled={backfilling}
                            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded px-3 py-2 text-sm"
                            data-testid="accounting-backfill"
                        >
                            {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            Backfill from Stripe
                        </button>
                        <button
                            type="button"
                            onClick={runRepair}
                            disabled={repairing}
                            className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 rounded px-3 py-2 text-sm"
                            data-testid="accounting-repair"
                            title="One-shot repair: re-shape legacy charge rows so they show up on this dashboard."
                        >
                            {repairing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            Repair legacy rows
                        </button>
                    </div>
                </div>

                {/* KPI tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Kpi label="Gross revenue" value={$(totals.gross_cents)} testid="kpi-gross" hint={`${totals.count || 0} events`} />
                    <Kpi label="Sales tax collected" value={$(totals.tax_cents)} testid="kpi-tax" hint="Pass-through liability — remit to WA DOR" />
                    <Kpi label="Stripe fees" value={$(totals.fee_cents)} testid="kpi-fee" hint="Deductible processing expense" />
                    <Kpi label="Net to bank" value={$(totals.net_cents)} testid="kpi-net" hint="gross − tax − fees" emphasis />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Kpi label="Refunds issued" value={$(totals.refund_cents)} testid="kpi-refunds" tone="amber" />
                    <Kpi label="Dispute losses" value={$(totals.dispute_cents)} testid="kpi-disputes" tone="red" />
                    <Kpi label="WA B&O estimate" value={$(wa.bo_total_estimate_cents)} testid="kpi-bo" hint={`Retail + Service (${((wa.bo_service_rate || 0) * 100).toFixed(2)}%)`} />
                    <Kpi label="Retained revenue" value={$((totals.gross_cents || 0) - (totals.tax_cents || 0) - (totals.fee_cents || 0) - (wa.bo_total_estimate_cents || 0))} testid="kpi-retained" hint="After taxes + fees" emphasis />
                </div>

                {/* Send accountant report for a specific month (Sachin 2026-09) */}
                <div className="mb-8">
                    <SendAccountantMonthCard />
                </div>

                {/* Live Stripe ↔ ledger reconciliation (2026-02-17) */}
                <div className="mb-8">
                    <StripeReconcileWidget initialDays={30} />
                </div>

                {/* Pack auto-renewal pipeline health (2026-02-17) */}
                <div className="mb-8">
                    <RenewalHealthWidget />
                </div>

                {/* Card-on-file pipeline health (2026-02-17) */}
                <div className="mb-8">
                    <CardOnFileHealthWidget />
                </div>

                {/* Stripe ↔ MaxSC reconciliation — finds Stripe subs that
                    aren't tracked in our DB (2026-02-17 Sam Schwartz case) */}
                <div className="mb-8">
                    <StripeReconciliationWidget />
                </div>

                {/* WA tax explainer */}
                <div className="bg-amber-950/30 border border-amber-900 rounded-lg p-4 mb-8 flex gap-3" data-testid="wa-tax-explainer">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-100/90">
                        <p className="font-semibold text-amber-300 mb-1">Washington B&O estimate</p>
                        <p>
                            Subscriptions/facility access classified as <b>Retailing</b> ({((wa.bo_retailing_rate || 0) * 100).toFixed(3)}%) ·
                            coaching/training/ad-hoc as <b>Service & Other Activities</b> ({((wa.bo_service_rate || 0) * 100).toFixed(2)}%).
                            Final classification is your CPA's call — these are estimates.
                        </p>
                        <p className="mt-1 text-xs text-amber-200/60">
                            Retailing gross {$(wa.retailing_gross_cents)} → {$(wa.bo_retailing_estimate_cents)} B&O ·
                            Service gross {$(wa.service_gross_cents)} → {$(wa.bo_service_estimate_cents)} B&O
                        </p>
                    </div>
                </div>

                {/* Category breakdown */}
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden mb-8" data-testid="category-breakdown">
                    <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-emerald-400" />
                        <h2 className="font-semibold">Revenue by category</h2>
                    </div>
                    {byCat.length === 0 ? (
                        <div className="px-5 py-12 text-center text-zinc-500 text-sm">
                            No revenue events in this window yet. Run the backfill or wait for Stripe charges.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-900 text-zinc-400">
                                <tr>
                                    <th className="text-left px-5 py-2">Category</th>
                                    <th className="text-right px-5 py-2">Gross</th>
                                    <th className="text-right px-5 py-2">Tax</th>
                                    <th className="text-right px-5 py-2">Fees</th>
                                    <th className="text-right px-5 py-2">Refunds</th>
                                    <th className="text-right px-5 py-2">Net</th>
                                    <th className="text-right px-5 py-2">#</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byCat.map(([cat, b]) => (
                                    <tr key={cat} className="border-t border-zinc-900" data-testid={`category-row-${cat}`}>
                                        <td className="px-5 py-2.5 capitalize">{cat.replace(/_/g, " ")}</td>
                                        <td className="px-5 py-2.5 text-right">{$(b.gross_cents)}</td>
                                        <td className="px-5 py-2.5 text-right text-zinc-400">{$(b.tax_cents)}</td>
                                        <td className="px-5 py-2.5 text-right text-zinc-400">{$(b.fee_cents)}</td>
                                        <td className="px-5 py-2.5 text-right text-amber-400">{$(b.refund_cents)}</td>
                                        <td className="px-5 py-2.5 text-right font-semibold">{$(b.net_cents)}</td>
                                        <td className="px-5 py-2.5 text-right text-zinc-500">{b.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Event ledger */}
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid="event-ledger">
                    <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                        <CalendarRange className="w-4 h-4 text-emerald-400" />
                        <h2 className="font-semibold">Event ledger</h2>
                        <span className="text-xs text-zinc-500">{events.length} events</span>
                    </div>
                    {events.length === 0 ? (
                        <div className="px-5 py-12 text-center text-zinc-500 text-sm">
                            No events. Backfill from Stripe to populate historical activity.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-900 text-zinc-400">
                                    <tr>
                                        <th className="text-left px-5 py-2">When</th>
                                        <th className="text-left px-5 py-2">Kind</th>
                                        <th className="text-left px-5 py-2">Category</th>
                                        <th className="text-right px-5 py-2">Gross</th>
                                        <th className="text-right px-5 py-2">Tax</th>
                                        <th className="text-right px-5 py-2">Fee</th>
                                        <th className="text-right px-5 py-2">Net</th>
                                        <th className="text-right px-5 py-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((ev) => (
                                        <tr key={ev.event_id} className="border-t border-zinc-900" data-testid={`event-row-${ev.event_id}`}>
                                            <td className="px-5 py-2.5 text-zinc-300">
                                                {new Date(ev.occurred_at).toLocaleString()}
                                            </td>
                                            <td className="px-5 py-2.5">
                                                <span className={`px-2 py-0.5 rounded text-xs ${KIND_TONE[ev.kind] || "bg-zinc-800 text-zinc-300"}`}>
                                                    {KIND_LABEL[ev.kind] || ev.kind}
                                                    {ev.kind === "manual_payment" && ev.metadata?.method ? ` · ${ev.metadata.method}` : ""}
                                                </span>
                                            </td>
                                            <td className="px-5 py-2.5 capitalize text-zinc-400">{(ev.sku_category || "—").replace(/_/g, " ")}</td>
                                            <td className="px-5 py-2.5 text-right">{$(ev.gross_cents)}</td>
                                            <td className="px-5 py-2.5 text-right text-zinc-400">{$(ev.tax_cents)}</td>
                                            <td className="px-5 py-2.5 text-right text-zinc-400">{$(ev.stripe_fee_cents)}</td>
                                            <td className="px-5 py-2.5 text-right font-semibold">{$(ev.net_cents)}</td>
                                            <td className="px-5 py-2.5 text-right">
                                                {ev.kind === "charge" && ev.gross_cents > 0 && ev.stripe_charge_id ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setRefundOpen({ event: ev })}
                                                        className="text-xs text-red-400 hover:text-red-300"
                                                        data-testid={`refund-btn-${ev.event_id}`}
                                                    >Refund</button>
                                                ) : ev.kind === "manual_payment" && !ev.voided ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => voidPayment(ev)}
                                                        className="text-xs text-zinc-400 hover:text-red-300"
                                                        data-testid={`void-btn-${ev.event_id}`}
                                                    >Void</button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {refundOpen && (
                <RefundModal
                    event={refundOpen.event}
                    onClose={() => setRefundOpen(null)}
                    onDone={() => { setRefundOpen(null); load(); }}
                />
            )}
            {logPayOpen && (
                <LogPaymentModal
                    onClose={() => setLogPayOpen(false)}
                    onDone={() => { setLogPayOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function Kpi({ label, value, hint, testid, emphasis, tone }) {
    const toneClass =
        tone === "amber" ? "border-amber-900/60" :
        tone === "red" ? "border-red-900/60" :
        emphasis ? "border-emerald-700" : "border-zinc-800";
    const valueColor =
        tone === "amber" ? "text-amber-300" :
        tone === "red" ? "text-red-300" :
        emphasis ? "text-emerald-300" : "text-white";
    return (
        <div data-testid={testid} className={`bg-zinc-950 border ${toneClass} rounded-lg p-4`}>
            <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</p>
            {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
        </div>
    );
}

function RefundModal({ event, onClose, onDone }) {
    const [amount, setAmount] = useState(((event.gross_cents - (event.tax_cents || 0)) / 100).toFixed(2));
    const [reason, setReason] = useState("requested_by_customer");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const cents = Math.round(parseFloat(amount) * 100);
        if (!cents || cents <= 0) { toast.error("Invalid amount"); return; }
        if (cents > event.gross_cents) { toast.error("Cannot refund more than the original charge"); return; }
        if (!window.confirm(`Refund ${(cents/100).toFixed(2)} on Stripe charge ${event.stripe_charge_id}? This is irreversible.`)) return;
        setBusy(true);
        try {
            await axios.post(`${API}/coach/accounting/refund`, {
                stripe_charge_id: event.stripe_charge_id,
                amount_cents: cents,
                reason,
                note,
            }, { withCredentials: true });
            toast.success("Refund issued");
            onDone();
        } catch (e) {
            toast.error("Refund failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="refund-modal" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-md w-full p-6">
                <h3 className="text-lg font-semibold mb-1">Refund charge</h3>
                <p className="text-xs text-zinc-500 mb-4 break-all">{event.stripe_charge_id}</p>
                <div className="space-y-3 text-sm">
                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Amount (USD)</label>
                        <input
                            data-testid="refund-amount"
                            type="number"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        />
                        <p className="text-xs text-zinc-500 mt-1">
                            Original: {$(event.gross_cents)} · Tax: {$(event.tax_cents)} · Net: {$(event.net_cents)}
                        </p>
                    </div>
                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Reason</label>
                        <select
                            data-testid="refund-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        >
                            <option value="requested_by_customer">Requested by customer</option>
                            <option value="duplicate">Duplicate</option>
                            <option value="fraudulent">Fraudulent</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Note (internal)</label>
                        <textarea
                            data-testid="refund-note"
                            rows="2"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="px-3 py-2 text-sm text-zinc-400 hover:text-white"
                        data-testid="refund-cancel"
                    >Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-3 py-2 text-sm bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded flex items-center gap-2"
                        data-testid="refund-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                        Issue refund
                    </button>
                </div>
            </div>
        </div>
    );
}


function LogPaymentModal({ onClose, onDone }) {
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("zelle");
    const [reference, setReference] = useState("");
    const [memo, setMemo] = useState("");
    const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
    const [taxInclusive, setTaxInclusive] = useState(false);
    const [clientQuery, setClientQuery] = useState("");
    const [clients, setClients] = useState([]);
    const [pickedClient, setPickedClient] = useState(null);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (pickedClient) return;
        if (!clientQuery || clientQuery.length < 2) { setClients([]); return; }
        let cancel = false;
        const t = setTimeout(async () => {
            setSearching(true);
            try {
                const r = await axios.get(`${API}/coach/leads`, {
                    params: { q: clientQuery }, withCredentials: true,
                });
                if (!cancel) setClients((r.data || []).slice(0, 8));
            } catch {/* ignore */}
            finally { if (!cancel) setSearching(false); }
        }, 250);
        return () => { cancel = true; clearTimeout(t); };
    }, [clientQuery, pickedClient]);

    const submit = async () => {
        const cents = Math.round(parseFloat(amount) * 100);
        if (!cents || cents <= 0) { toast.error("Enter a valid amount"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/coach/accounting/manual-payment`, {
                amount_cents: cents,
                method,
                reference,
                memo,
                user_id: pickedClient?.user_id || null,
                occurred_at: occurredAt,
                tax_inclusive: taxInclusive,
            }, { withCredentials: true });
            toast.success(`Logged ${method.toUpperCase()} payment of $${(cents/100).toFixed(2)}`);
            onDone();
        } catch (e) {
            toast.error("Couldn't log payment", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="log-payment-modal" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold">Log a payment</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">Zelle, check, cash, Venmo, or other off-Stripe payment.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="log-payment-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">Amount (USD)</label>
                            <input
                                data-testid="log-pay-amount"
                                type="number"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">Method</label>
                            <select
                                data-testid="log-pay-method"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                            >
                                <option value="zelle">Zelle</option>
                                <option value="check">Check</option>
                                <option value="cash">Cash</option>
                                <option value="venmo">Venmo</option>
                                <option value="ach">ACH</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Date received</label>
                        <input
                            data-testid="log-pay-date"
                            type="date"
                            value={occurredAt}
                            onChange={(e) => setOccurredAt(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">
                            {method === "check" ? "Check #" : method === "zelle" ? "Zelle confirmation #" : "Reference"}
                            <span className="text-zinc-600"> (optional)</span>
                        </label>
                        <input
                            data-testid="log-pay-reference"
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder={method === "check" ? "1042" : "ABC123"}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Client <span className="text-zinc-600">(optional, helps bookkeeper)</span></label>
                        {pickedClient ? (
                            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-700 rounded px-3 py-2" data-testid="log-pay-client-chip">
                                <div>
                                    <div className="text-sm">{pickedClient.name || pickedClient.email}</div>
                                    <div className="text-xs text-zinc-500">{pickedClient.email}</div>
                                </div>
                                <button type="button" onClick={() => { setPickedClient(null); setClientQuery(""); }} className="text-xs text-zinc-400 hover:text-white">Change</button>
                            </div>
                        ) : (
                            <div className="relative">
                                <input
                                    data-testid="log-pay-client-search"
                                    type="text"
                                    value={clientQuery}
                                    onChange={(e) => setClientQuery(e.target.value)}
                                    placeholder="Search by name or email…"
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                                />
                                {clientQuery && clients.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded shadow-lg max-h-56 overflow-y-auto">
                                        {clients.map((c) => (
                                            <button
                                                key={c.user_id}
                                                type="button"
                                                onClick={() => { setPickedClient(c); setClients([]); }}
                                                className="w-full text-left px-3 py-2 hover:bg-zinc-800 border-b border-zinc-800 last:border-b-0"
                                                data-testid={`log-pay-client-${c.user_id}`}
                                            >
                                                <div className="text-sm">{c.name || "(no name)"}</div>
                                                <div className="text-xs text-zinc-500">{c.email}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {searching && <div className="text-xs text-zinc-500 mt-1">Searching…</div>}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Memo <span className="text-zinc-600">(what was it for?)</span></label>
                        <input
                            data-testid="log-pay-memo"
                            type="text"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            placeholder="March membership · 10-pack · etc."
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                        />
                    </div>

                    <label className="flex items-center gap-2 text-xs text-zinc-400" data-testid="log-pay-tax-inclusive">
                        <input
                            type="checkbox"
                            checked={taxInclusive}
                            onChange={(e) => setTaxInclusive(e.target.checked)}
                        />
                        Amount includes 10.3% WA sales tax (extract it for the ledger)
                    </label>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="px-3 py-2 text-sm text-zinc-400 hover:text-white"
                    >Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !amount}
                        className="px-4 py-2 text-sm bg-blue-700 hover:bg-blue-600 disabled:opacity-50 rounded flex items-center gap-2"
                        data-testid="log-pay-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Log payment
                    </button>
                </div>
            </div>
        </div>
    );
}
